import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { SERVICES, SERVICE_TYPES, FAQS } from "../lib/data";

const PageHero = ({ overline, title, subtitle }) => (
  <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
    <div className="mx-auto max-w-7xl">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{overline}</span>
      <h1 className="mt-3 max-w-3xl font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
        {title}
      </h1>
      {subtitle && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">{subtitle}</p>}
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <SEO
        title="Cleaning Services"
        description="Residential and small-office cleaning services in Hamilton — deep cleans, recurring maintenance, one-time and move in/out cleaning. Free home visit before pricing."
        image="/svc-residential.jpg"
      />
      <PageHero
        overline="Our Services"
        title="Detail-focused cleaning for homes & small offices"
        subtitle="Every home and workspace is different. We build each clean around your space and priorities — and we always start with a free home visit before any pricing."
      />

      <div className="border-b border-slate-200 bg-muted/60 px-5 py-4 md:px-10">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Curious what a clean typically costs in our area? We use fair market pricing.</p>
          <Link
            to="/guides/house-cleaning-cost-hamilton"
            data-testid="services-cost-guide-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:underline"
          >
            Read our honest price guide →
          </Link>
        </div>
      </div>

      <section className="px-5 py-20 md:px-10">
        <div className="mx-auto max-w-7xl space-y-20">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`grid items-center gap-12 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-lg">
                <img src={s.image} alt={s.title} loading="lazy" decoding="async" className="h-[360px] w-full object-cover" />
              </div>
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <s.icon className="h-6 w-6" />
                </div>
                <h2 className="mt-5 font-heading text-3xl font-medium text-slate-900">{s.title}</h2>
                <p className="mt-4 text-lg text-slate-600">{s.summary}</p>
                <ul className="mt-6 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-slate-700">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                        <Check className="h-4 w-4 text-primary" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white px-5 py-20 md:px-10">
        <div className="mx-auto max-w-7xl">
          <h2 className="max-w-2xl font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
            Ways we can help
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_TYPES.map((t, i) => (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary/20 text-slate-900">
                  <t.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-base text-slate-600">
            Not sure which is the right fit? That's completely okay — we'll talk it through together
            during your free home visit and help you find what works best for your home and your
            routine.
          </p>
          <p className="mt-4 max-w-3xl text-sm text-slate-500">
            Pricing depends on your space and needs. To keep every quote fair and accurate, we
            arrange a free home visit before sharing pricing — we never quote guaranteed
            prices sight unseen.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-200 px-5 py-20 md:px-10">
        <div className="mx-auto max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Good to know</span>
          <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-heading text-lg font-medium text-slate-900">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand />
    </>
  );
}
